/**
 * Number and money formatting for currency display.
 *
 * Deliberately takes `lang` as an argument every time. `toLocaleString()` with
 * no locale follows the DEVICE locale, not the app's i18n locale, so a French
 * page on an American laptop formats as en-US and nobody notices until a
 * screenshot arrives. Passing it explicitly makes that impossible to forget.
 */
export function formatNumber(
  value: number,
  lang: string,
  options?: Intl.NumberFormatOptions,
): string {
  if (!Number.isFinite(value)) return String(value);
  try {
    return new Intl.NumberFormat(latnLocale(lang), options).format(value);
  } catch {
    return String(value);
  }
}

/**
 * The locale tag with Latin digits pinned (`-u-nu-latn`).
 *
 * Digits stay Latin/Western in every locale, ar included. A bare
 * `Intl.NumberFormat("ar")` happens to give Latin digits today because CLDR
 * defaults Moroccan Arabic to `latn`, but that is a data default, not a
 * guarantee. Use this for any `Intl.NumberFormat` or `Intl.DateTimeFormat`
 * call that prints a number.
 *
 * Appends to an existing `-u-` extension rather than adding a second one
 * (which is invalid), and replaces a numbering system the tag already names.
 */
export function latnLocale(lang: string | undefined | null): string {
  const tag = (lang ?? "").trim() || "en";
  const lower = tag.toLowerCase();
  const u = lower.indexOf("-u-");
  if (u === -1) return `${tag}-u-nu-latn`;
  if (/-nu-[a-z0-9]{3,8}/i.test(tag.slice(u))) {
    return tag.slice(0, u) + tag.slice(u).replace(/-nu-[a-z0-9]{3,8}/i, "-nu-latn");
  }
  return `${tag.slice(0, u)}-u-nu-latn${tag.slice(u + 2)}`;
}

export function formatPrice(value: number, currency: string, lang: string): string {
  return `${formatNumber(value, lang, { maximumFractionDigits: 0 })} ${currency}`;
}

/**
 * Localized currency label. The ISO code stays as-is in every Latin-script
 * locale; ar gets the Arabic name, because "MAD" reads as Latin glyphs dropped
 * into Arabic copy.
 *
 * Only the LABEL localises. The digits stay Latin/Western in every locale, ar
 * included — Morocco writes prices as 1,250 and that also keeps prices
 * comparable across locales.
 */
export function currencyLabel(currency: string, lang: string): string {
  if (currency !== "MAD") return currency;
  if (lang === "ar") return "درهم";
  return "MAD";
}

/**
 * Formats a value and labels it MAD, whatever the guest picked in the header.
 *
 * NOT a general price formatter. It is for the ONE case where dirhams are the
 * correct and only answer: the printed receipt, where the amount is the cash
 * the guest hands the provider on the day. Every other surface must use
 * `useDisplayPrice` / `useDisplayPriceParts`.
 *
 * Read as "format a price" it quietly pinned six live surfaces to MAD — the
 * home hero deck, the home collections row, reel cards and the blog inline
 * activity cards — so that a guest browsing in USD saw dollars on the cards
 * and dirhams in the hero above them. Audited and cleared 2026-08-21; the only
 * remaining callers are in `buildReceiptHtml`.
 */
export function formatMad(value: number, lang: string): string {
  return `${formatNumber(value, lang, { maximumFractionDigits: 0 })} ${currencyLabel("MAD", lang)}`;
}
